import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { PageActionModalComponent } from '@clinic-platform/ui-components';

export type ClinicDetailsModalMode = 'create' | 'edit';

export type ClinicDetailsFormValue = {
  name: string;
  city?: string;
  address?: string;
};

@Component({
  selector: 'app-clinic-details-modal',
  standalone: true,
  imports: [
    PageActionModalComponent,
    ReactiveFormsModule,
  ],
  template: `
    <lib-page-action-modal
      [isOpen]="isOpen"
      [title]="title"
      [subtitle]="subtitle"
      width="720px"
      [canClose]="!isSaving"
      [canSubmit]="canSubmit"
      (close)="closeRequested()"
      (submit)="saveRequested()"
    >
      <form [formGroup]="form" (ngSubmit)="saveRequested()" class="clinic-details-modal__grid">
        <label>
          {{ clinicLabel }} name
          <input type="text" formControlName="name" data-modal-primary-focus="true" />
        </label>

        <label>
          City
          <input type="text" formControlName="city" />
        </label>

        <label>
          Address
          <input type="text" formControlName="address" />
        </label>

        <div class="clinic-details-modal__actions">
          <button type="button" class="clinic-details-modal__action" [disabled]="isSaving" (click)="closeRequested()">
            Cancel
          </button>
          <button
            type="submit"
            class="clinic-details-modal__action clinic-details-modal__action--primary"
            [disabled]="!canSubmit"
          >
            {{ submitLabel }}
          </button>
        </div>
      </form>
    </lib-page-action-modal>
  `,
  styles: [`
    .clinic-details-modal__grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      align-items: end;
    }

    .clinic-details-modal__grid label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 13px;
      color: #374151;
      font-weight: 600;
    }

    .clinic-details-modal__grid input {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 10px;
      box-sizing: border-box;
      font-size: 14px;
      color: #111827;
      background: #ffffff;
    }

    .clinic-details-modal__actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;
    }

    .clinic-details-modal__action {
      height: 32px;
      border-radius: 8px;
      border: 1px solid #2563eb;
      background: #ffffff;
      color: #2563eb;
      padding: 0 10px;
      font-size: 13px;
      cursor: pointer;
    }

    .clinic-details-modal__action:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }

    .clinic-details-modal__action--primary {
      background: #2563eb;
      color: #ffffff;
    }

    @media (max-width: 900px) {
      .clinic-details-modal__grid {
        grid-template-columns: 1fr;
      }

      .clinic-details-modal__actions {
        flex-direction: column-reverse;
      }

      .clinic-details-modal__action {
        width: 100%;
      }
    }
  `],
})
export class ClinicDetailsModalComponent implements OnChanges
{
  @Input() public isOpen = false;
  @Input() public mode: ClinicDetailsModalMode = 'create';
  @Input() public clinicLabel = 'Clinic';
  @Input() public clinicLabelLower = 'clinic';
  @Input() public isSaving = false;
  @Input() public value: ClinicDetailsFormValue | null = null;

  @Output() public readonly close = new EventEmitter<void>();
  @Output() public readonly save = new EventEmitter<ClinicDetailsFormValue>();

  private readonly formBuilder = inject(FormBuilder);

  protected readonly form = this.formBuilder.group({
    name: this.formBuilder.nonNullable.control('', [Validators.required, Validators.maxLength(255)]),
    city: this.formBuilder.nonNullable.control('', [Validators.maxLength(255)]),
    address: this.formBuilder.nonNullable.control('', [Validators.maxLength(500)]),
  });

  protected get title(): string {
    return this.mode === 'create'
      ? `Create ${this.clinicLabelLower}`
      : `Edit ${this.clinicLabelLower}`;
  }

  protected get subtitle(): string {
    return this.mode === 'create'
      ? `Add a new ${this.clinicLabelLower} with its baseline location metadata.`
      : `Update core ${this.clinicLabelLower} information without leaving the settings context.`;
  }

  protected get submitLabel(): string {
    if (this.isSaving) {
      return this.mode === 'create' ? 'Creating...' : 'Saving...';
    }
    return this.mode === 'create' ? `Create ${this.clinicLabelLower}` : 'Save';
  }

  protected get canSubmit(): boolean {
    return !this.form.invalid && !this.isSaving;
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    if (changes['isOpen']?.currentValue === true || changes['value']) {
      this.form.reset({
        name: this.value?.name ?? '',
        city: this.value?.city ?? '',
        address: this.value?.address ?? '',
      }, { emitEvent: false });
    }
  }

  protected closeRequested(): void
  {
    if (this.isSaving) {
      return;
    }
    this.close.emit();
  }

  protected saveRequested(): void
  {
    if (!this.canSubmit) {
      return;
    }

    const value = this.form.getRawValue();
    this.save.emit({
      name: value.name.trim(),
      city: value.city.trim() || undefined,
      address: value.address.trim() || undefined,
    });
  }
}
